import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanySelector from '../components/collateral/CompanySelector';
import ProspectSelector from '../components/collateral/ProspectSelector';
import UseCaseSelector from '../components/collateral/UseCaseSelector';
import CollateralList from '../components/collateral/CollateralList';
import GenerationLoadingOverlay from '../components/collateral/GenerationLoadingOverlay';
import { apiService } from '../service/Api';
import { DEFAULT_GENERATION_STEPS, DEFAULT_GENERATION_CONFIG } from '../utils/generationSteps';

const CollateralGeneration = () => {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [collateralDescription, setCollateralDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCollateralList, setShowCollateralList] = useState(false);
  const [showEditIntelligence, setShowEditIntelligence] = useState(false);
  const [intelligenceJson, setIntelligenceJson] = useState('');
  const [updatingIntelligence, setUpdatingIntelligence] = useState(false);

  const handleGenerateCollateral = async () => {
    setError(null);

    if (!selectedCompany) {
      setError('Please select or create a company');
      return;
    }

    if (!selectedProspect) {
      setError('Please select or create a prospect');
      return;
    }

    if (!collateralDescription.trim()) {
      setError('Please provide a description of the collateral required');
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      // Prepare collateral data
      const collateralData = {
        company_id: selectedCompany.id,
        prospect_id: selectedProspect.id,
        use_case: collateralDescription.trim()
      };

      // Create collateral
      const response = await apiService.createCollateral(collateralData);

      if (response.data.success) {
        // Navigate to collateral view or show success message
        const createdCollateral = response.data.data;
        console.log("createdCollateral", createdCollateral);
        
        // Wait for minimum loading time to show progress (if API is faster than 4 min)
        const minLoadingTime = DEFAULT_GENERATION_CONFIG.duration;
        const elapsed = Date.now() - startTime;
        // Ensure minimum loading time is respected
        if (elapsed < minLoadingTime) {
          await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsed));
        }

        setLoading(false);
        navigate(`/tailspin-agentic/${createdCollateral.slug}`);
      } else {
        setError(response.data.error || 'Failed to create collateral');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating collateral:', err);
      setError(err.response?.data?.error || 'Failed to generate collateral');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditIntelligence = async () => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    setShowEditIntelligence(true);
    setError(null);

    try {
      // Fetch current company data to get existing intelligence
      const response = await apiService.getCompanyById(selectedCompany.id);
      if (response.data.success && response.data.data) {
        const company = response.data.data;
        const currentIntelligence = company.intelligence_json || {};
        setIntelligenceJson(JSON.stringify(currentIntelligence, null, 2));
      } else {
        setIntelligenceJson('{}');
      }
    } catch (err) {
      console.error('Error fetching company intelligence:', err);
      setIntelligenceJson('{}');
    }
  };

  const handleUpdateIntelligence = async () => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    setUpdatingIntelligence(true);
    setError(null);

    try {
      // Parse and validate JSON
      let parsedJson;
      try {
        parsedJson = JSON.parse(intelligenceJson);
      } catch (err) {
        setError('Invalid JSON format. Please check your JSON syntax.');
        setUpdatingIntelligence(false);
        return;
      }

      // Update company intelligence
      const response = await apiService.updateCompanyIntelligence(
        selectedCompany.id,
        parsedJson
      );

      if (response.data.success) {
        // Update selected company with new intelligence
        setSelectedCompany({
          ...selectedCompany,
          intelligence_json: parsedJson
        });
        setShowEditIntelligence(false);
        setIntelligenceJson('');
      } else {
        setError(response.data.error || response.data.message || 'Failed to update intelligence');
      }
    } catch (err) {
      console.error('Error updating company intelligence:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update intelligence');
    } finally {
      setUpdatingIntelligence(false);
    }
  };

  const handleCloseEditIntelligence = () => {
    setShowEditIntelligence(false);
    setIntelligenceJson('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Collateral Generation</h1>
          <p className="text-gray-600">
            Create professional B2B collateral for your prospects and companies
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Selection */}
            <div className="space-y-4">
              <CompanySelector
                selectedCompany={selectedCompany}
                onSelectCompany={setSelectedCompany}
                onCreateCompany={(company) => {
                  setSelectedCompany(company);
                  setSelectedProspect(null); // Reset prospect when company changes
                }}
              />
              
              {/* Edit Intelligence Button */}
              {selectedCompany && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <button
                    type="button"
                    onClick={handleOpenEditIntelligence}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Company Intelligence
                  </button>
                </div>
              )}
            </div>

            {/* Prospect Selection */}
            <ProspectSelector
              selectedCompany={selectedCompany}
              selectedProspect={selectedProspect}
              onSelectProspect={setSelectedProspect}
              onCreateProspect={setSelectedProspect}
            />

            {/* Collateral Description */}
            <UseCaseSelector
              description={collateralDescription}
              onDescriptionChange={setCollateralDescription}
            />

            {/* Generate Button */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <button
                onClick={handleGenerateCollateral}
                disabled={loading || !selectedCompany || !selectedProspect || !collateralDescription.trim()}
                className={`w-full px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                  loading || !selectedCompany || !selectedProspect || !collateralDescription.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  'Generate Collateral'
                )}
              </button>
            </div>
          </div>

          {/* Sidebar - Collateral List */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <button
                onClick={() => setShowCollateralList(!showCollateralList)}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                {showCollateralList ? 'Hide' : 'Show'} Collateral Library
              </button>
            </div>
            
            {showCollateralList && (
              <CollateralList
                companyId={selectedCompany?.id}
                onSelectCollateral={(collateral) => {
               
                  navigate(`/tailspin-agentic/${collateral.slug}`);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Generation Loading Overlay */}
      <GenerationLoadingOverlay
        isVisible={loading}
        duration={DEFAULT_GENERATION_CONFIG.duration}
        steps={DEFAULT_GENERATION_STEPS}
        stepDuration={DEFAULT_GENERATION_CONFIG.stepDuration}
      />

      {/* Edit Intelligence Modal */}
      {showEditIntelligence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Company Intelligence
              </h2>
              <button
                type="button"
                onClick={handleCloseEditIntelligence}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intelligence JSON
                </label>
                <textarea
                  value={intelligenceJson}
                  onChange={(e) => setIntelligenceJson(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={20}
                  placeholder='{"key": "value"}'
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter valid JSON format. This will update the company's intelligence data.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseEditIntelligence}
                disabled={updatingIntelligence}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateIntelligence}
                disabled={updatingIntelligence || !intelligenceJson.trim()}
                className={`px-6 py-2 rounded-lg font-semibold text-white transition-all ${
                  updatingIntelligence || !intelligenceJson.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg'
                }`}
              >
                {updatingIntelligence ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  'Update Intelligence'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollateralGeneration;
